import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Download, Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/App';
import { Section } from '@/components/surveyAccess/accessProfileFields';
import { cleanMapping, mappableBranches } from '@/components/surveyAccess/BranchMappingEditor';

const HEADER = 'Existing branch in satisFIND,External / POS Store Code,Access Method';
const esc = (v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
const METHOD_TEXT = { qr: 'QR', manual: 'Numeric' };

const downloadTemplate = (branches, mapping) => {
  const list = mappableBranches(branches);
  const nameCount = list.reduce((c, b) => ({ ...c, [b.name]: (c[b.name] || 0) + 1 }), {});
  const lines = [HEADER, ...list.map(b => {
    const e = (mapping || []).find(m => m.branch_id === b.branch_id) || {};
    const label = nameCount[b.name] > 1 && b.branch_code ? b.branch_code : b.name;
    return [label, e.external_id || '', METHOD_TEXT[e.access_method] || ''].map(v => esc(String(v))).join(',');
  })];
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
  a.download = 'branch-access-mapping.csv';
  a.click();
  URL.revokeObjectURL(a.href);
};

const ImportPreview = ({ preview, onApply, onCancel }) => {
  const bad = preview.rows.filter(r => !r.ok);
  return (
    <div className="space-y-2" data-testid="access-import-preview">
      <p className="text-xs text-[#334155]">
        {preview.fileName}: <b data-testid="access-import-total">{preview.summary.total}</b> rows ·{' '}
        <b className="text-[#047857]" data-testid="access-import-valid">{preview.summary.valid} ready</b> ·{' '}
        <b className={bad.length ? 'text-[#B91C1C]' : 'text-[#64748B]'} data-testid="access-import-invalid">{preview.summary.invalid} with problems (skipped)</b>
      </p>
      {bad.length > 0 && (
        <div className="max-h-48 overflow-y-auto rounded border border-[#FECACA] bg-[#FEF2F2]" data-testid="access-import-invalid-rows">
          {bad.map(r => (
            <p key={r.line} className="px-3 py-1.5 text-[11px] text-[#7F1D1D] border-b border-[#FEE2E2] last:border-0" data-testid={`access-import-row-${r.line}`}>
              <b>Row {r.line}</b> — {r.branch_input || '(no branch)'} · {r.external_id || '(no code)'} · {r.method_input || '(no method)'}: {r.errors.join(' ')}
            </p>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Button type="button" size="sm" onClick={onApply} disabled={!preview.summary.valid} className="bg-[#1A3E6F] text-white" data-testid="access-import-apply-btn">
          Use {preview.summary.valid} mappings
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel} data-testid="access-import-cancel-btn">Cancel</Button>
      </div>
    </div>
  );
};

// Phase 4B — CSV bulk mapping: validated server-side, applied to the draft, saved with the profile.
export const BranchImportPanel = ({ form, set, branches }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    setPreview(null);
    if (!/\.csv$/i.test(file.name)) { setError('Please choose a .csv file.'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/access-profiles/branch-import/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ csv_text: await file.text(), mapping: cleanMapping(form.branch_mapping, form.methods), methods: form.methods }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setPreview({ ...data, fileName: file.name });
      else setError(typeof data.detail === 'string' ? data.detail : 'Could not read the file.');
    } catch { setError('Could not read the file.'); }
    setBusy(false);
  };

  const apply = () => {
    set(['branch_mapping'], preview.mapping);
    toast.success(`${preview.summary.valid} branch mappings added — click Save profile to keep them.`);
    setPreview(null);
  };

  return (
    <Section title="Bulk import (CSV)" testid="access-import-panel">
      <p className="text-[11px] text-[#64748B]">
        Columns: Existing branch in satisFIND (name or branch code) · External / POS Store Code · Access Method (QR or Numeric).
        Every row is checked before anything changes.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => downloadTemplate(branches, form.branch_mapping)} data-testid="access-import-template-btn">
          <Download className="w-3.5 h-3.5 mr-1" /> Download template
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="access-import-upload-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Upload CSV
        </Button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="access-import-file-input" />
      </div>
      {error && <p role="alert" className="text-xs text-[#B91C1C]" data-testid="access-import-error">{error}</p>}
      {preview && <ImportPreview preview={preview} onApply={apply} onCancel={() => setPreview(null)} />}
    </Section>
  );
};
